import { StarPRNT, Emulation } from '@awesome-cordova-plugins/star-prnt';

import { withLock } from 'src/utils/printer-lock';
import { OrderData } from 'src/utils/Services/OrderService';

// Transform legacy 'walk-in' to 'to-go' for backward compatibility
const transformCustomerType = (type?: string): 'to-go' | 'dine-in' => {
  if (type === 'walk-in') return 'to-go';
  return (type as 'to-go' | 'dine-in') || 'to-go';
};

const PORT = 'BT:TSP100';
const EMU = Emulation.StarGraphic;

function orderReceiptCanvas(order: OrderData, width = 576): string {
  const fontPx = 68;
  const smallFontPx = 36; // Much smaller font for customer name and date/time
  const font = `600 ${fontPx}px monospace`;
  const smallFont = `600 ${smallFontPx}px monospace`;
  const lineH = fontPx + 10; // Reduced spacing between lines
  const smallLineH = smallFontPx + 4; // Smaller line height for small font
  const pad = 20; // Reduced padding

  // Helper function to wrap text with hyphenation support
  const wrapText = (text: string, maxWidth: number, ctx: CanvasRenderingContext2D): string[] => {
    const effectiveWidth = maxWidth - pad * 2;
    const wrappedLines: string[] = [];
    const words = text.split(' ');
    let currentLine = '';

    const breakWord = (
      word: string,
      availableWidth: number
    ): { broken: string; remainder: string } => {
      // Try to find a good break point for the word
      let breakPoint = word.length;

      // Start from the maximum characters that might fit and work backwards
      for (let i = Math.min(word.length - 1, Math.floor(word.length * 0.75)); i > 0; i -= 1) {
        const testWord = `${word.substring(0, i)}-`;
        const metrics = ctx.measureText(testWord);
        if (metrics.width <= availableWidth) {
          breakPoint = i;
          break;
        }
      }

      // Ensure we break at least 2 characters in
      if (breakPoint < 2 && word.length > 2) {
        breakPoint = 2;
      }

      return {
        broken: `${word.substring(0, breakPoint)}-`,
        remainder: word.substring(breakPoint),
      };
    };

    words.forEach((word) => {
      if (!word) return; // Skip empty words

      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const metrics = ctx.measureText(testLine);

      if (metrics.width > effectiveWidth) {
        if (currentLine) {
          // Current line is not empty, push it and start new line with word
          wrappedLines.push(currentLine);

          // Check if the word itself needs breaking
          const wordMetrics = ctx.measureText(word);
          if (wordMetrics.width > effectiveWidth) {
            // Word is too long, needs breaking
            let remainingWord = word;
            while (remainingWord) {
              const { broken, remainder } = breakWord(remainingWord, effectiveWidth);
              wrappedLines.push(broken);
              remainingWord = remainder;

              // Check if remainder fits on one line
              if (remainder) {
                const remainderMetrics = ctx.measureText(remainder);
                if (remainderMetrics.width <= effectiveWidth) {
                  currentLine = remainder;
                  remainingWord = '';
                }
              }
            }
          } else {
            currentLine = word;
          }
        } else {
          // Current line is empty, word itself is too long
          let remainingWord = word;
          while (remainingWord) {
            const remainderMetrics = ctx.measureText(remainingWord);
            if (remainderMetrics.width <= effectiveWidth) {
              currentLine = remainingWord;
              break;
            }

            const { broken, remainder } = breakWord(remainingWord, effectiveWidth);
            wrappedLines.push(broken);
            remainingWord = remainder;
          }
        }
      } else {
        currentLine = testLine;
      }
    });

    if (currentLine) {
      wrappedLines.push(currentLine);
    }

    return wrappedLines.length > 0 ? wrappedLines : [text];
  };

  // Create temporary canvas to measure text
  const tempCanvas = document.createElement('canvas');
  const tempCtx = tempCanvas.getContext('2d')!;
  tempCtx.font = font;

  const lines: Array<{ text: string; useSmallFont?: boolean }> = [];

  lines.push({ text: `ORDER #${order.orderNumber}` });

  // Add table number if it exists
  if (order.orderDetails?.tableNumber) {
    lines.push({ text: `Table ${order.orderDetails.tableNumber}` });
  }

  lines.push({ text: '====================' });

  order.items.forEach((item, index) => {
    const qty = item.quantity.toString();
    const { name } = item;
    const itemText = `${qty} ${name}`;

    // Wrap long item names
    const wrappedItem = wrapText(itemText, width, tempCtx);
    wrappedItem.forEach((line) => lines.push({ text: line }));

    // Add modifiers if they exist
    if (item.metadata?.selectedModifierData && item.metadata.selectedModifierData.length > 0) {
      const modifiers = item.metadata.selectedModifierData
        .flatMap((group: any) =>
          group.selected.map((modifier: any) => {
            // Include quantity if greater than 1
            if (modifier.quantity && modifier.quantity > 1) {
              return `${modifier.name} (×${modifier.quantity})`;
            }
            return modifier.name;
          })
        )
        .filter((modifierName: string) => modifierName); // Filter out any empty names

      if (modifiers.length > 0) {
        // Add each modifier on its own line with indentation
        modifiers.forEach((modifier: string) => {
          const modifierText = `  + ${modifier}`;
          const wrappedModifier = wrapText(modifierText, width, tempCtx);
          wrappedModifier.forEach((line) => lines.push({ text: line }));
        });
      }
    }

    // Add special instructions if they exist
    const specialInstructions = (item.metadata as any)?.specialInstructions as string | undefined;
    if (specialInstructions) {
      const wrappedInstruction = wrapText(specialInstructions, width - pad * 4, tempCtx);
      wrappedInstruction.forEach((segment) => {
        lines.push({ text: `** ${segment} **` });
      });
    }

    // Add separator after each item except the last one
    if (index < order.items.length - 1) {
      lines.push({ text: '--------------------' });
    }
  });

  lines.push({ text: '====================' });

  // Add order notes if they exist
  if (order.orderDetails?.orderNotes) {
    lines.push({ text: 'Notes:' });
    const wrappedNotes = wrapText(order.orderDetails.orderNotes, width, tempCtx);
    wrappedNotes.forEach((line) => lines.push({ text: line }));
    lines.push({ text: '--------------------' });
  }

  // Use orderDetails for customer name if available, otherwise fall back to customerInfo
  let customerName: string | undefined;
  if (order.orderDetails?.customerName) {
    ({ customerName } = order.orderDetails);
  } else if (
    order.customerInfo &&
    typeof order.customerInfo !== 'boolean' &&
    order.customerInfo.name
  ) {
    customerName = order.customerInfo.name;
  }

  if (customerName) {
    const customerText = `Customer: ${customerName}`;
    // Need to wrap with smaller font context
    tempCtx.font = smallFont;
    const wrappedCustomer = wrapText(customerText, width, tempCtx);
    tempCtx.font = font; // Reset to normal font
    wrappedCustomer.forEach((line) => lines.push({ text: line, useSmallFont: true }));
  }

  const tsToDate = (value: unknown): Date => {
    if (!value) return new Date();
    if (value instanceof Date) return value;
    if (typeof (value as any).toDate === 'function') return (value as any).toDate();
    return new Date();
  };

  let date = tsToDate(order.createdAt);
  if (!order.createdAt && order.details?.date) {
    date = new Date(order.details.date as unknown as string);
  }
  const timeString = date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const dateTimeString = `Date: ${timeString} ${month}/${day}`;

  lines.push({ text: dateTimeString, useSmallFont: true });

  // Add customer type at the bottom
  if (order.orderDetails?.customerType) {
    lines.push({ text: '--------------------' });
    const transformedType = transformCustomerType(order.orderDetails.customerType);
    const customerTypeText = transformedType === 'dine-in' ? 'DINE-IN' : 'TO-GO';
    lines.push({ text: customerTypeText });
  }

  // Calculate total height considering different font sizes
  let totalHeight = pad * 2;
  lines.forEach((lineObj) => {
    totalHeight += lineObj.useSmallFont ? smallLineH : lineH;
  });

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = totalHeight;

  const ctx = canvas.getContext('2d')!;

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.fillStyle = '#000000';
  ctx.textBaseline = 'top';
  ctx.imageSmoothingEnabled = false;

  let y = pad;
  let inNotesSection = false;

  lines.forEach((lineObj) => {
    const line = lineObj.text;

    // Set the appropriate font
    ctx.font = lineObj.useSmallFont ? smallFont : font;

    // Track if we're in the notes section
    if (line === 'Notes:') {
      inNotesSection = true;
    } else if (line === '--------------------' && inNotesSection) {
      inNotesSection = false;
    }

    if (line.includes('====================') || line.includes('--------------------')) {
      // Center separator lines
      ctx.textAlign = 'center';
      ctx.fillText(line, width / 2, y);
    } else if (
      line.startsWith('Customer:') ||
      line.startsWith('Date:') ||
      line.startsWith('Notes:') ||
      line.match(/^\d{1,3}\s+/) ||
      line.startsWith('  +') ||
      line.startsWith('**') ||
      inNotesSection
    ) {
      // Left align for customer name, date/time, notes header, notes content, items, and modifiers
      ctx.textAlign = 'left';
      ctx.fillText(line, pad, y);
    } else if (
      line.startsWith('ORDER #') ||
      line.startsWith('Table ') ||
      line === 'DINE-IN' ||
      line === 'TO-GO' ||
      line === 'WALK-IN' // Keep for backward compatibility
    ) {
      // Center the order number, table number, and customer type
      ctx.textAlign = 'center';
      ctx.fillText(line, width / 2, y);
    } else {
      // Default center alignment for any other text
      ctx.textAlign = 'center';
      ctx.fillText(line, width / 2, y);
    }

    // Adjust y position based on font size used
    y += lineObj.useSmallFont ? smallLineH : lineH;
  });

  return canvas.toDataURL('image/png');
}

export async function printOrderReceiptOneBitmap(
  order: OrderData,
  opts: { port?: string; paperWidth?: 576 | 384 | 832 } = {}
) {
  const WIDTH = opts.paperWidth ?? 576;
  const dataUri = orderReceiptCanvas(order, WIDTH);

  console.log('Printing order receipt:', order.orderNumber);

  await withLock(async () => {
    await StarPRNT.printImage(opts.port ?? PORT, EMU, {
      uri: dataUri,
      paperWidth: WIDTH,
      cutReceipt: true,
    });
  });
}
